/**
 * Pure tool-call classifier + label functions.
 *
 * Extracted verbatim from `pages/Workspace.tsx` (crucible plan 001
 * Step 5.1). These are side-effect-free helpers that inspect a
 * `ToolCallEntry` and decide how the chat renderer should treat it
 * (soft-amber recoverable, subdued pedagogical, friendly label,
 * new-UI routine grouping). No React, no IPC, no component scope —
 * unit-tested directly and re-exported from `Workspace.tsx` for
 * back-compat with existing test imports.
 */
import type { ToolCallEntry } from './types.js';

/**
 * Issue #200: detect the SDK's "Edit was a no-op because old_string
 * and new_string are identical" error. The Edit tool emits this as
 * a `<tool_use_error>` payload that the reducer marks as
 * `status: 'error'` and the renderer would otherwise paint with the
 * red \u2717 icon \u2014 even though nothing went wrong, the agent simply
 * tried to overwrite a value with itself. Treating it as a soft
 * yellow "no change" indicator avoids the panic-inducing red and
 * keeps real Edit failures (file not found, permission denied,
 * etc.) visually distinct.
 *
 * Match is intentionally narrow \u2014 the literal substring the SDK
 * emits. Anything else with `<tool_use_error>` falls through and
 * stays red.
 *
 * Pure helper exported for unit tests.
 */
const EDIT_NOOP_MARKER =
  '<tool_use_error>No changes to make: old_string and new_string are exactly the same.</tool_use_error>';

export function isEditNoopFailure(toolCall: ToolCallEntry): boolean {
  if (toolCall.name !== 'Edit') return false;
  if (toolCall.status !== 'error') return false;
  const out = toolCall.output ?? '';
  return out.includes(EDIT_NOOP_MARKER);
}

/**
 * Issue #216: detect the SDK's "Edit found N matches but `replace_all`
 * is false" error. The agent self-recovers from this on the very next
 * turn by re-firing the same Edit with `replace_all: true`. Rendering
 * it as a panic-inducing red \u2717 between two green successes is
 * visible noise that makes the build look rougher than it is \u2014
 * particularly on the CEO build-a-game video. Treat it as a soft
 * amber dot, the same way #200 treats the `old_string === new_string`
 * no-op.
 *
 * Format pinned against the literal SDK template (verified by
 * inspection of the bundled `claude.exe` shipped with
 * `@anthropic-ai/claude-agent-sdk@0.2.x`):
 *
 *   `Found ${W} matches of the string to replace, but replace_all is
 *    false. To replace all occurrences, set replace_all to true. ...`
 *
 * The match is anchored against the substring `matches of the string
 * to replace, but replace_all is false` so a different number of
 * matches still classifies, but a free-text chat bubble that happens
 * to mention "replace_all" in passing does not over-match. We also
 * require the `<tool_use_error>` wrapper so we never reclassify a
 * normal user message that quotes the error text.
 *
 * Pure helper exported for unit tests.
 */
const EDIT_REPLACE_ALL_MARKER = 'matches of the string to replace, but replace_all is false';

export function isEditReplaceAllRecoverableFailure(toolCall: ToolCallEntry): boolean {
  if (toolCall.name !== 'Edit') return false;
  if (toolCall.status !== 'error') return false;
  const out = toolCall.output ?? '';
  // Require BOTH the SDK's tool-error wrapper AND the marker substring
  // so that a chat bubble or assistant text quoting the error does
  // not accidentally trigger amber treatment.
  return out.includes('<tool_use_error>') && out.includes(EDIT_REPLACE_ALL_MARKER);
}

/**
 * Issue #216: detect the agent firing a `Grep` (or other path-taking
 * search tool) without the required `path` argument. The Anthropic
 * SDK rejects this with an `InputValidationError` and the agent
 * self-recovers on the next turn by re-firing with the path filled
 * in. Render as amber \u25cb rather than red \u2717 so the user doesn't
 * read it as a real failure.
 *
 * Format pinned against the literal SDK template (verified by
 * inspection of `claude.exe`):
 *
 *   `<tool_use_error>InputValidationError: Grep failed due to the
 *    following issue:\nThe required parameter \`path\` is missing
 *    </tool_use_error>`
 *
 * Multiple missing params get joined as plural "issues" so we don't
 * pin against the singular wording. We DO require:
 *   1. The `<tool_use_error>` wrapper (so a chat bubble can't trigger).
 *   2. The literal `InputValidationError` token (so a real
 *      tool-execution failure can't trigger).
 *   3. The substring `` The required parameter `path` is missing ``
 *      (the exact format produced by the SDK's `hQH` Zod-error
 *      formatter for a missing required `path` field).
 *
 * Scoped to `Grep` for now \u2014 that's the shape Pratik observed in
 * the Earth-Invaders local build. Other path-taking tools (Glob,
 * Read) follow the same self-recovery pattern but we'll only widen
 * the scope when we observe them in the wild, to keep the
 * over-match risk low.
 *
 * Pure helper exported for unit tests.
 */
const GREP_MISSING_PATH_MARKER = 'The required parameter `path` is missing';

export function isGrepMissingPathFailure(toolCall: ToolCallEntry): boolean {
  if (toolCall.name !== 'Grep') return false;
  if (toolCall.status !== 'error') return false;
  const out = toolCall.output ?? '';
  return (
    out.includes('<tool_use_error>') &&
    out.includes('InputValidationError') &&
    out.includes(GREP_MISSING_PATH_MARKER)
  );
}

/**
 * Issue #224: detect the legacy build-game interview preflight read
 * against `.foundry/build-game-interview.json` on a fresh game. That
 * exact missing-file read is expected and the agent self-recovers.
 *
 * Format pinned against the literal SDK template (verified by
 * inspection of the bundled `claude.exe`):
 *
 *   `<tool_use_error>File does not exist. Note: your current
 *    working directory is /path/to/cwd</tool_use_error>`
 *
 * Match is conservative \u2014 we require:
 *   1. tool name = `Read` (other tools have different self-recovery
 *      shapes and are matched by their own helpers).
 *   2. The exact legacy interview path, either game-relative or as
 *      the trailing absolute path.
 *   3. The `<tool_use_error>` wrapper, so a chat bubble that quotes
 *      the error in passing cannot trigger amber treatment.
 *   4. The literal `File does not exist.` marker (with the trailing
 *      period \u2014 that's part of the SDK template).
 *
 * OTHER Read failures \u2014 `EACCES: permission denied`,
 * `EISDIR: illegal operation on a directory`, etc. \u2014 do NOT contain
 * either marker and stay red so the user can see what actually went
 * wrong.
 *
 * Pure helper exported for unit tests.
 */
const READ_FILE_NOT_FOUND_MARKER = 'File does not exist.';
const LEGACY_BUILD_GAME_INTERVIEW_READ_PATH = '.foundry/build-game-interview.json';

function isExpectedLegacyBuildGameInterviewRead(toolCall: ToolCallEntry): boolean {
  const input = toolCall.input as Record<string, unknown>;
  const rawPath =
    typeof input.file_path === 'string'
      ? input.file_path
      : typeof input.path === 'string'
        ? input.path
        : '';
  const normalised = rawPath.replace(/\\/g, '/');
  return (
    normalised === LEGACY_BUILD_GAME_INTERVIEW_READ_PATH ||
    normalised.endsWith(`/${LEGACY_BUILD_GAME_INTERVIEW_READ_PATH}`)
  );
}

export function isReadFileNotFoundFailure(toolCall: ToolCallEntry): boolean {
  if (toolCall.name !== 'Read') return false;
  if (toolCall.status !== 'error') return false;
  if (!isExpectedLegacyBuildGameInterviewRead(toolCall)) return false;
  const out = toolCall.output ?? '';
  const trimmed = out.trim();
  if (trimmed.startsWith(READ_FILE_NOT_FOUND_MARKER)) return true;
  if (!out.includes('<tool_use_error>')) return false;
  return out.includes(READ_FILE_NOT_FOUND_MARKER);
}

/**
 * Issue #511 — detect the Edit tool's "String to replace not found"
 * failure shape so it renders as an info-chip rather than the same
 * red envelope used for genuine permission denials. The SDK emits
 * this whenever an Edit's `old_string` argument doesn't appear
 * verbatim in the target file (whitespace differs, agent edits from
 * memory rather than a fresh Read, file moved between read+edit).
 *
 * Pre-fix the envelope was indistinguishable from a permission
 * denial — both wrapped in `<tool_use_error>...</tool_use_error>`
 * markup. That conflation:
 *   - misleads users into thinking they need to allowlist something
 *     (no — Edit is always allowed; the issue is the `old_string`)
 *   - wastes agent turns when the agent retries the wrong fix
 *
 * Format pinned against the literal SDK template (verified against
 * `claude.exe` bundled with `@anthropic-ai/claude-agent-sdk@0.2.x`):
 *
 *   `<tool_use_error>String to replace not found in file.
 *    String: ${oldString}</tool_use_error>`
 *
 * Distinct from `isEditReplaceAllRecoverableFailure` (which fires on
 * "Found N matches ... replace_all is false") — that one means the
 * substring DOES exist but matches too many places. This helper
 * means the substring doesn't exist at all.
 *
 * Match requires:
 *   1. tool name = `Edit` (other tools have different self-recovery shapes).
 *   2. The `<tool_use_error>` wrapper, so a chat bubble that quotes
 *      the error text in passing cannot trigger info-chip treatment.
 *   3. The literal `String to replace not found in file.` marker
 *      (with trailing period — pinned against the SDK template).
 *
 * Pure helper exported for unit tests.
 */
const EDIT_MATCH_FAILED_MARKER = 'String to replace not found in file.';

export function isEditMatchFailedFailure(toolCall: ToolCallEntry): boolean {
  if (toolCall.name !== 'Edit') return false;
  if (toolCall.status !== 'error') return false;
  const out = toolCall.output ?? '';
  return out.includes('<tool_use_error>') && out.includes(EDIT_MATCH_FAILED_MARKER);
}

/**
 * Issue #239: detect Bash commands that the Crucible allowlist denies
 * BEFORE the shell even sees them. The agent self-recovers on the
 * very next turn (uses Write to create a directory instead of
 * `mkdir`, abandons a `pnpm install` attempt and moves on, picks a
 * different binary that's actually on the allowlist, etc). Render as
 * amber \u25cb so the user doesn't read these structurally-fine
 * recoveries as scary failures during the CEO build-a-game video.
 *
 * Match conditions (Bash tool only) \u2014 each entry pins the FULL
 * canonical deny-reason text emitted by `evaluateBashCommand` in
 * `agent-permissions.ts`. We deliberately use the long phrasing
 * (rather than a fragment) so a real test-runner failure that quotes
 * the deny string in its assertion output cannot trigger amber
 * treatment \u2014 Codex / Cursor Bugbot review (2026-05-05) caught that
 * the existing `agent-permissions.test.ts` snapshots assert these
 * exact strings, so a `pnpm test` failure exercising those tests
 * would print them in its stderr and a fragment-based match would
 * silently amber-render the genuine failure.
 *
 *   1. `is only allowed via '--version' / '-v'` \u2014 the
 *      pnpm/npm/node version-flag restriction (line 1052 in
 *      `agent-permissions.ts`). The `'--version' / '-v'` suffix
 *      pins past arbitrary "is only allowed via \u2026" prose.
 *   2. `Bash command rejected \u2014 contains banned metacharacter` \u2014
 *      the metachar ban (line 968). Uses the em-dash from the deny
 *      message so an unrelated mention of "banned metacharacter"
 *      cannot trigger.
 *   3. `Read-only enumeration doesn't need pipes, redirects` \u2014
 *      the verbose tail of the metachar deny; matched separately
 *      so a future SDK drift that re-emits only the verbose half
 *      still classifies. The trailing `, redirects` keeps it
 *      specific.
 *   4. `is not on the allowlist.` \u2014 the unknown-binary deny (line
 *      1059) that Pratik observed for `mkdir` before hello-weekend
 *      v0.12.1 (issue #239 reads this loosely as "unknown command";
 *      the actual literal text is "is not on the allowlist."). The
 *      trailing period anchors against quotes / paraphrasing and
 *      Cursor Bugbot review (2026-05-05) flagged the bare
 *      "unknown command" form as too generic.
 *
 * Path-restriction denials emitted by `evaluateToolPolicy` for
 * non-Bash tools (`Tool '\u2026' on '\u2026' denied by Crucible policy: \u2026`)
 * are deliberately NOT included \u2014 `evaluateToolPolicy` short-circuits
 * to `evaluateBashCommand` for Bash and never produces that string
 * for the Bash branch (Codex P2 catch, 2026-05-05). If we ever want
 * to soften those denials they need their own helper keyed off the
 * Read / Grep / Glob tool name.
 *
 * Belt-and-braces: we ALSO refuse to classify when the output looks
 * like a test-runner / build-tool failure (`AssertionError`,
 * `ELIFECYCLE`, ` FAIL `, `Test failed.`, `expected `, `> expect(`,
 * `.toBe(`, `.toContain(`, `.toEqual(`). canUseTool deny messages
 * never contain those tokens \u2014 they're emitted by
 * `evaluateBashCommand` BEFORE the shell runs. A test-runner failure
 * that asserts deny text would print BOTH the deny text AND the
 * test-runner framing, so the negative check kicks in and the
 * failure stays red. This is the second line of defence on top of
 * the full-phrase markers above (Codex P2 catch, 2026-05-05).
 *
 * Note on the `<tool_use_error>` envelope: the other recoverable
 * helpers (`isEditReplaceAllRecoverableFailure`,
 * `isGrepMissingPathFailure`, `isReadFileNotFoundFailure`) require
 * the `<tool_use_error>` XML wrapper because their target shapes are
 * SDK-internal validation errors that ALWAYS arrive in that
 * envelope. Crucible's `canUseTool` deny path takes a different
 * route \u2014 the deny `message` flows through the SDK as a
 * `tool_result` with `is_error: true` and the raw reason as content,
 * with no XML wrapper. Requiring `<tool_use_error>` here would miss
 * every legitimate deny. The combination of full-phrase markers AND
 * the test-runner negative check is the sound substitute.
 *
 * Pure helper exported for unit tests.
 */
const BASH_DENIED_BY_POLICY_MARKERS = [
  "is only allowed via '--version' / '-v'",
  'Bash command rejected \u2014 contains banned metacharacter',
  "Read-only enumeration doesn't need pipes, redirects",
  'is not on the allowlist.',
] as const;

/**
 * Negative check \u2014 if the Bash output contains any of these
 * test-runner / build-tool framing tokens it's a real exit-code-1
 * failure rather than a Crucible policy deny. canUseTool deny
 * messages are pure prose emitted by the policy gate BEFORE the
 * shell runs and never contain these markers. This guards against
 * the case (Codex P2, 2026-05-05) where a `pnpm test` run exercising
 * `agent-permissions.test.ts` would print the literal deny strings
 * inside vitest assertion output.
 */
const BASH_TEST_RUNNER_MARKERS = [
  'AssertionError',
  'ELIFECYCLE',
  ' FAIL ',
  'Test failed.',
  'expected ',
  '> expect(',
  '.toBe(',
  '.toContain(',
  '.toEqual(',
] as const;

export function isBashPolicyDenialFailure(toolCall: ToolCallEntry): boolean {
  if (toolCall.name !== 'Bash') return false;
  if (toolCall.status !== 'error') return false;
  const out = toolCall.output ?? '';
  if (BASH_TEST_RUNNER_MARKERS.some((marker) => out.includes(marker))) {
    return false;
  }
  return BASH_DENIED_BY_POLICY_MARKERS.some((marker) => out.includes(marker));
}

/**
 * Issue #216 / #224 / #239: union of "agent self-recovers on the next
 * turn" tool errors. Rendering the recoverable shapes as a soft amber
 * \u25cb (rather than red \u2717) keeps real failures visually distinct
 * while stripping noise from the chat transcript. Each entry is
 * matched by its own narrow helper above; this helper is the single
 * render-time gate.
 *
 * If you add a new shape, also add a regression test pinning the
 * exact tool error text \u2014 we have been bitten before by over-broad
 * substring matches papering over real failures.
 */
export function isRecoverableToolError(toolCall: ToolCallEntry): boolean {
  return (
    isEditNoopFailure(toolCall) ||
    isEditReplaceAllRecoverableFailure(toolCall) ||
    isEditMatchFailedFailure(toolCall) ||
    isGrepMissingPathFailure(toolCall) ||
    isReadFileNotFoundFailure(toolCall) ||
    isBashPolicyDenialFailure(toolCall)
  );
}

/**
 * Short label rendered under the recoverable-tool-error icon. Each
 * shape gets its own copy so the user can see WHY the agent had to
 * retry. Returns `null` for non-recoverable calls so `ToolCallCard`
 * can fall through to the standard rendering.
 */
export function recoverableToolErrorLabel(toolCall: ToolCallEntry): string | null {
  if (isEditNoopFailure(toolCall)) return 'No change \u2014 value already matched.';
  if (isEditReplaceAllRecoverableFailure(toolCall)) {
    return 'Multiple matches \u2014 agent will retry with replace_all.';
  }
  // Issue #511. The Edit `old_string` didn't appear verbatim in the
  // target file \u2014 distinct shape from permission denials. Render as
  // an info chip so the user (and any future agent reading the
  // transcript) can tell at a glance this isn't a permissions issue.
  if (isEditMatchFailedFailure(toolCall)) {
    return 'Text not found \u2014 agent will re-read the file and retry.';
  }
  if (isGrepMissingPathFailure(toolCall)) {
    return 'Missing path \u2014 agent will retry with the right parameters.';
  }
  if (isReadFileNotFoundFailure(toolCall)) {
    return 'File not found \u2014 agent will continue without this file.';
  }
  if (isBashPolicyDenialFailure(toolCall)) {
    return 'Denied by policy \u2014 agent will pick a different approach.';
  }
  return null;
}

/**
 * Issue #200: detect Edit calls that target Foundry's build-game
 * interview-state JSON. The `/build-game` skill records the user's
 * Phase A answers by running a cascade of small Edits against
 * `.foundry/build-game-interview.json`. Surfacing the raw success
 * blob ("The file ... has been updated successfully") five times in
 * a row reads as noise \u2014 the user just clicked an answer button and
 * doesn't care about the file. Replace the output text with a
 * friendlier short label.
 *
 * Heuristic: name must be `Edit` AND `file_path` must point at
 * `build-game-interview.json` somewhere under `.foundry/` (i.e.
 * `<game-root>/.foundry/build-game-interview.json` after backslash
 * normalisation). Other `.foundry/*` writes \u2014 `dispatch/**` for
 * Phase B subagent staging and `dev-status.json` for `/diagnose` \u2014
 * are legitimate non-answer Edits and MUST keep their raw output
 * so the user can see what went on. Codex review (2026-05-04)
 * caught this \u2014 the original heuristic matched `(^|/)\\.foundry/`
 * which over-matched. Pinned negatively in the test suite.
 *
 * Pure helper exported for unit tests.
 */
const BUILD_GAME_INTERVIEW_BASENAME = '.foundry/build-game-interview.json';
/**
 * v9 \u00a75 Decision B / Phase 0a \u00a711.1 \u2014 under `CRUCIBLE_CLOUD_VM_V1=1`
 * the canonical interview file lives under `.foundry/persistent/`
 * (Codex round 8 P3). The agent Edit tool calls land at this
 * post-split path, so the matcher needs both shapes to keep the
 * friendly "Saved your answer." label in cloud-mode.
 */
const BUILD_GAME_INTERVIEW_BASENAME_PERSISTENT = '.foundry/persistent/build-game-interview.json';

export function buildGameInterviewEditLabel(toolCall: ToolCallEntry): string | null {
  if (toolCall.name !== 'Edit') return null;
  // Errors must NOT be papered over with a friendly success label \u2014
  // a genuine interview-state Edit failure (file missing, permission
  // denied) should still surface its raw output so the user can see
  // what actually went wrong. The only error this layer softens is
  // the dedicated "Edit was a no-op" case (handled separately by
  // `isEditNoopFailure`).
  if (toolCall.status === 'error') return null;
  const filePath = toolCall.input.file_path;
  if (typeof filePath !== 'string') return null;
  // Normalise path separators so a Windows-flavoured `.foundry\\u2026`
  // path (extremely unlikely from Anthropic's SDK, but cheap to
  // defend against) still matches.
  const normalised = filePath.replace(/\\/g, '/');
  // Match the canonical build-game-interview file at any depth
  // (game root may itself live under a workspace directory). Accept
  // BOTH the legacy `.foundry/build-game-interview.json` shape
  // (current flag-OFF behaviour) and the post-split
  // `.foundry/persistent/build-game-interview.json` shape (cloud-vm
  // flag ON). Reject every other `.foundry/*` file so dispatch
  // staging files and `dev-status.json` keep their raw output.
  const isInterviewFile =
    normalised === BUILD_GAME_INTERVIEW_BASENAME ||
    normalised === BUILD_GAME_INTERVIEW_BASENAME_PERSISTENT ||
    normalised.endsWith(`/${BUILD_GAME_INTERVIEW_BASENAME}`) ||
    normalised.endsWith(`/${BUILD_GAME_INTERVIEW_BASENAME_PERSISTENT}`);
  if (!isInterviewFile) return null;
  if (toolCall.status === 'running') return 'Recording your answer\u2026';
  return 'Saved your answer.';
}

export function buildToolCallFriendlyLabel(toolCall: ToolCallEntry): string | null {
  return buildGameInterviewEditLabel(toolCall);
}

// ---------------------------------------------------------------------------
// Issue #619 \u2014 pedagogical tool-result errors
//
// Tool-result errors with self-correction copy (Read file-too-large,
// Glob too-many-results, etc.) used to render as the same alarming red
// \u274c chip as genuine failures. The agent self-recovers on the next call
// WITHOUT user intervention \u2014 the red chip is misleading.
//
// This section adds a third tier to the routed-vs-blocked classification
// from PR #586:
//
//   1. `policy_routed`  \u2014 permission-gate nudge, rendered by RoutedHintChip
//      (set via `policy_decision` side-channel from agent-adapter).
//   2. `recoverable`    \u2014 agent self-recovers; amber \u25e6 chip (isRecoverableToolError).
//   3. `pedagogical`    \u2014 tool-result carries recovery guidance; subdued
//      blue/slate info chip (isPedagogicalToolError, this section).
//
// Fallback: anything NOT matched by the pedagogical patterns stays on
// the existing red-chip / amber-recoverable paths \u2014 no regression.
// ---------------------------------------------------------------------------

/**
 * Issue #619 \u2014 Read tool: the agent called Read on a file whose
 * token count exceeds the SDK's per-call limit. The error body
 * tells the agent exactly how to recover (use `offset`/`limit`
 * parameters or search instead). The agent ALWAYS self-recovers \u2014
 * this is pedagogical guidance, not a failure.
 *
 * Pinned against the literal SDK template (verified against
 * `@anthropic-ai/claude-agent-sdk` bundled in the Foundry binary):
 *
 *   `File content (N tokens) exceeds maximum allowed tokens (25000).
 *    Use offset and limit parameters to read specific portions of the
 *    file, or search for specific content instead of reading the whole
 *    file.`
 *
 * We require ALL of:
 *   1. tool name = `Read` (avoid matching other tools whose errors
 *      happen to mention "tokens").
 *   2. `exceeds maximum allowed tokens` \u2014 the core discriminator.
 *   3. `Use offset and limit parameters` \u2014 the recovery copy that
 *      confirms this is a pedagogical limit hit, not a permissions
 *      error.
 *
 * The pattern does NOT require the `<tool_use_error>` XML wrapper
 * because the SDK emits this as a plain `tool_result` with
 * `is_error: true` (same shape as the Bash-policy-deny path \u2014 no
 * SDK-internal XML envelope).
 *
 * Pure helper exported for unit tests.
 */
const READ_TOO_LARGE_MARKER_1 = 'exceeds maximum allowed tokens';
const READ_TOO_LARGE_MARKER_2 = 'Use offset and limit parameters';

export function isReadTooLargeError(toolCall: ToolCallEntry): boolean {
  if (toolCall.name !== 'Read') return false;
  if (toolCall.status !== 'error') return false;
  const out = toolCall.output ?? '';
  return out.includes(READ_TOO_LARGE_MARKER_1) && out.includes(READ_TOO_LARGE_MARKER_2);
}

/**
 * Issue #619 \u2014 Glob tool: the agent's pattern matched more results
 * than the SDK's truncation limit. The SDK returns a truncated list
 * with a trailing note. The agent self-recovers by narrowing the
 * pattern or using `path` to scope the search.
 *
 * Pinned against the SDK message shape:
 *
 *   `Note: only showing first N results. Use the path parameter to
 *    narrow results, or use a more specific pattern.`
 *
 * We require:
 *   1. tool name = `Glob`.
 *   2. The `only showing first` truncation marker.
 *   3. The `Use the path parameter` recovery copy.
 *
 * Note: Glob truncation MAY arrive as `is_error: false` in some SDK
 * versions (a warning, not a hard failure). This PR only classifies
 * `status: 'error'` because that is the only shape observed in the
 * wild so far. If a future SDK version emits truncation as a success-
 * status result, this helper will need to be widened. The doc-comment
 * previously said "status: 'error' OR status: 'success'" \u2014 that was
 * aspirational and incorrect; corrected here (Codex round-1).
 *
 * Pure helper exported for unit tests.
 */
const GLOB_TOO_MANY_MARKER_1 = 'only showing first';
const GLOB_TOO_MANY_MARKER_2 = 'Use the path parameter';

export function isGlobTooManyResultsError(toolCall: ToolCallEntry): boolean {
  if (toolCall.name !== 'Glob') return false;
  if (toolCall.status !== 'error') return false;
  const out = toolCall.output ?? '';
  return out.includes(GLOB_TOO_MANY_MARKER_1) && out.includes(GLOB_TOO_MANY_MARKER_2);
}

/**
 * Issue #619 \u2014 Bash tool: the OS shell emitted a "Did you mean: <X>?"
 * suggestion immediately after a `command not found` line. This is
 * the POSIX shell / zsh / bash correction hint for a TYPO-ed binary
 * name (e.g. `gti` \u2192 `git`). The agent self-recovers on the next call
 * by using the suggested binary.
 *
 * Why this is safe to classify as pedagogical:
 *   - The OS shell only emits "Did you mean: <X>?" on the SAME line
 *     (or the following line) as the `<cmd>: command not found` shell
 *     message, and ONLY when the shell finds a near-match binary.
 *   - Real build/CI failures that mention "command not found" from a
 *     subprocess AND a "Did you mean" from a package manager (e.g.
 *     `npm ... Did you mean "\u2026"?`) contain very different surrounding
 *     prose. Codex round-1 flagged the bare two-substring match as
 *     too broad; the fix is:
 *       1. Require the OS shell's canonical `command not found` format:
 *          `": command not found"` (colon-space, matching the shell's
 *          `<cmd>: command not found` shape; npm/pnpm/cargo emit `not found`
 *          without the colon-prefixed command name in that exact form).
 *       2. Require `"Did you mean: "` (colon after "mean") \u2014 the shell's
 *          suggestion format. npm suggests `"did you mean \u2026?"` (lowercase,
 *          no colon); pnpm uses `"Did you mean \u2026?"` (no colon). Adding the
 *          colon avoids false-positives from package-manager "did you mean"
 *          messages that contain different prose.
 *
 * Belt-and-braces: also exclude test-runner framing tokens (same guard
 * as the Bash-policy-deny path above).
 *
 * Pure helper exported for unit tests.
 */
const BASH_COMMAND_NOT_FOUND_MARKER = ': command not found';
const BASH_DID_YOU_MEAN_MARKER = 'Did you mean: ';

export function isBashDidYouMeanError(toolCall: ToolCallEntry): boolean {
  if (toolCall.name !== 'Bash') return false;
  if (toolCall.status !== 'error') return false;
  const out = toolCall.output ?? '';
  // Guard: test-runner framing means the output is a real test failure
  // quoting error text, not an actual shell suggestion.
  if (BASH_TEST_RUNNER_MARKERS.some((marker) => out.includes(marker))) {
    return false;
  }
  return out.includes(BASH_COMMAND_NOT_FOUND_MARKER) && out.includes(BASH_DID_YOU_MEAN_MARKER);
}

/**
 * Issue #619 \u2014 union discriminator for all pedagogical tool-result
 * errors. A tool-result error is "pedagogical" when the output
 * body carries self-correction guidance the AGENT can act on
 * without any user intervention.
 *
 * Visual contract: rendered as a subdued blue/slate info chip
 * (`PedagogicalToolErrorChip`) rather than the existing red \u274c chip.
 * The recovery copy is still visible (click-to-expand) so the
 * curious user can see what happened, but the chip does NOT alarm
 * them.
 *
 * Fallback: any error NOT matched here stays on the existing red /
 * amber-recoverable render paths \u2014 no regression.
 *
 * Pure helper exported for unit tests.
 */
export function isPedagogicalToolError(toolCall: ToolCallEntry): boolean {
  return (
    isReadTooLargeError(toolCall) ||
    isGlobTooManyResultsError(toolCall) ||
    isBashDidYouMeanError(toolCall)
  );
}

/**
 * Short label for the closed-shape of `PedagogicalToolErrorChip`.
 * Each pattern gets a human-readable one-liner so the user
 * understands at a glance WHY the agent had to adapt, without
 * reading the full recovery copy. Returns `null` for non-pedagogical
 * calls (caller guards with `isPedagogicalToolError` first).
 *
 * Pure helper exported for unit tests.
 */
export function pedagogicalToolErrorLabel(toolCall: ToolCallEntry): string | null {
  if (isReadTooLargeError(toolCall)) {
    return 'File too large \u2014 agent will read in sections.';
  }
  if (isGlobTooManyResultsError(toolCall)) {
    return 'Too many matches \u2014 agent will narrow the pattern.';
  }
  if (isBashDidYouMeanError(toolCall)) {
    return 'Command not found \u2014 agent will use the suggested binary.';
  }
  return null;
}

export function toolVerbPast(name: string): string {
  const map: Record<string, string> = {
    write_file: 'Wrote',
    read_file: 'Read',
    run_command: 'Ran',
    list_files: 'Listed',
    Write: 'Wrote',
    Read: 'Read',
    Edit: 'Edited',
    Bash: 'Ran',
    Grep: 'Searched',
    Glob: 'Listed',
    Task: 'Spawned',
  };
  return map[name] ?? name;
}

export function toolVerbPresent(name: string): string {
  const map: Record<string, string> = {
    write_file: 'Writing',
    read_file: 'Reading',
    run_command: 'Running',
    list_files: 'Listing',
    Write: 'Writing',
    Read: 'Reading',
    Edit: 'Editing',
    Bash: 'Running',
    Grep: 'Searching',
    Glob: 'Listing',
    Task: 'Spawning',
  };
  return map[name] ?? name;
}

export function toolSubjectText(toolCall: ToolCallEntry): string {
  const { input } = toolCall;
  const path = (input.path ?? input.file_path ?? '') as string;
  if (path) return path;
  const cmd = (input.command ?? '') as string;
  if (cmd) return cmd;
  const pattern = (input.pattern ?? '') as string;
  if (pattern) return pattern;
  return '';
}

export type NewUiToolCallDisplaySegment =
  | { kind: 'routine-group'; key: string; toolCalls: readonly ToolCallEntry[] }
  | { kind: 'single'; toolCall: ToolCallEntry };

const NEW_UI_ROUTINE_TOOL_NAMES = new Set([
  'Bash',
  'Read',
  'Grep',
  'Glob',
  'LS',
  'Write',
  'Edit',
  'MultiEdit',
  'Search',
  'run_command',
  'read_file',
  'write_file',
  'list_files',
]);

export function groupNewUiToolCallsForDisplay(
  toolCalls: readonly ToolCallEntry[],
): NewUiToolCallDisplaySegment[] {
  const segments: NewUiToolCallDisplaySegment[] = [];
  let pendingRoutine: ToolCallEntry[] = [];
  const flushPendingRoutine = (): void => {
    if (pendingRoutine.length === 0) return;
    segments.push({
      kind: 'routine-group',
      key: `routine-${pendingRoutine[0]?.id ?? segments.length}`,
      toolCalls: pendingRoutine,
    });
    pendingRoutine = [];
  };
  for (const toolCall of toolCalls) {
    if (shouldCollapseRoutineToolCall(toolCall)) {
      pendingRoutine.push(toolCall);
      continue;
    }
    flushPendingRoutine();
    segments.push({ kind: 'single', toolCall });
  }
  flushPendingRoutine();
  return segments;
}

function shouldCollapseRoutineToolCall(toolCall: ToolCallEntry): boolean {
  if (toolCall.status !== 'success') return false;
  if (toolCall.name === 'AskUserQuestion') return false;
  if (toolCall.policyKind) return false;
  return NEW_UI_ROUTINE_TOOL_NAMES.has(toolCall.name);
}
